/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  BEResponseString,
  DeleteBackAttivitaIdAllegatiIdParams,
  GetBackAttivitaIdAllegatiIdContenutoBinarioParams,
  PostBackAttivitaIdAllegatiIdContenutoBinarioBody,
  PostBackAttivitaIdAllegatiIdContenutoBinarioParams,
  PutBackAttivitaIdAllegatiIdParams,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Modifica sul database alcuni dati del record associato all'allegato
 * @summary Modifica dati allegato
 */
export const putBackAttivitaIdAllegatiId = (
  idAllegato: string,
  params: PutBackAttivitaIdAllegatiIdParams,
  idTaskInstance = 'procedimento',
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati/${idAllegato}`,
      method: 'put',
      params,
    },
    options,
  );
};

export const getPutBackAttivitaIdAllegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdAllegatiId>>,
    TError,
    {
      idAllegato: string;
      params: PutBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackAttivitaIdAllegatiId>>,
  TError,
  {
    idAllegato: string;
    params: PutBackAttivitaIdAllegatiIdParams;
    idTaskInstance?: string;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackAttivitaIdAllegatiId>>,
    {
      idAllegato: string;
      params: PutBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    }
  > = props => {
    const { idAllegato, params, idTaskInstance } = props ?? {};

    return putBackAttivitaIdAllegatiId(
      idAllegato,
      params,
      idTaskInstance,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackAttivitaIdAllegatiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackAttivitaIdAllegatiId>>
>;

export type PutBackAttivitaIdAllegatiIdMutationError = ErrorType<unknown>;

/**
 * @summary Modifica dati allegato
 */
export const usePutBackAttivitaIdAllegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackAttivitaIdAllegatiId>>,
    TError,
    {
      idAllegato: string;
      params: PutBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackAttivitaIdAllegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Effettua la cancellazione di un allegato
 * @summary Cancellazione allegato
 */
export const deleteBackAttivitaIdAllegatiId = (
  idAllegato: string,
  params?: DeleteBackAttivitaIdAllegatiIdParams,
  idTaskInstance = 'procedimento',
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<BEResponseString>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati/${idAllegato}`,
      method: 'delete',
      params,
    },
    options,
  );
};

export const getDeleteBackAttivitaIdAllegatiIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdAllegatiId>>,
    TError,
    {
      idAllegato: string;
      params?: DeleteBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackAttivitaIdAllegatiId>>,
  TError,
  {
    idAllegato: string;
    params?: DeleteBackAttivitaIdAllegatiIdParams;
    idTaskInstance?: string;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackAttivitaIdAllegatiId>>,
    {
      idAllegato: string;
      params?: DeleteBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    }
  > = props => {
    const { idAllegato, params, idTaskInstance } = props ?? {};

    return deleteBackAttivitaIdAllegatiId(
      idAllegato,
      params,
      idTaskInstance,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackAttivitaIdAllegatiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackAttivitaIdAllegatiId>>
>;

export type DeleteBackAttivitaIdAllegatiIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancellazione allegato
 */
export const useDeleteBackAttivitaIdAllegatiId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackAttivitaIdAllegatiId>>,
    TError,
    {
      idAllegato: string;
      params?: DeleteBackAttivitaIdAllegatiIdParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackAttivitaIdAllegatiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Effettua l'upload di un allegato in formato binario
 * @summary Upload allegato
 */
export const postBackAttivitaIdAllegatiIdContenutoBinario = (
  idTipoAllegato: string,
  postBackAttivitaIdAllegatiIdContenutoBinarioBody: PostBackAttivitaIdAllegatiIdContenutoBinarioBody,
  params: PostBackAttivitaIdAllegatiIdContenutoBinarioParams,
  idTaskInstance = 'procedimento',
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append(
    'file',
    postBackAttivitaIdAllegatiIdContenutoBinarioBody.file,
  );

  return apiClientRequest<BEResponseString>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati/${idTipoAllegato}/contenutoBinario`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostBackAttivitaIdAllegatiIdContenutoBinarioMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdAllegatiIdContenutoBinario>>,
    TError,
    {
      idTipoAllegato: string;
      data: PostBackAttivitaIdAllegatiIdContenutoBinarioBody;
      params: PostBackAttivitaIdAllegatiIdContenutoBinarioParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackAttivitaIdAllegatiIdContenutoBinario>>,
  TError,
  {
    idTipoAllegato: string;
    data: PostBackAttivitaIdAllegatiIdContenutoBinarioBody;
    params: PostBackAttivitaIdAllegatiIdContenutoBinarioParams;
    idTaskInstance?: string;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackAttivitaIdAllegatiIdContenutoBinario>>,
    {
      idTipoAllegato: string;
      data: PostBackAttivitaIdAllegatiIdContenutoBinarioBody;
      params: PostBackAttivitaIdAllegatiIdContenutoBinarioParams;
      idTaskInstance?: string;
    }
  > = props => {
    const { idTipoAllegato, data, params, idTaskInstance } = props ?? {};

    return postBackAttivitaIdAllegatiIdContenutoBinario(
      idTipoAllegato,
      data,
      params,
      idTaskInstance,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackAttivitaIdAllegatiIdContenutoBinarioMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postBackAttivitaIdAllegatiIdContenutoBinario>>
  >;
export type PostBackAttivitaIdAllegatiIdContenutoBinarioMutationBody =
  PostBackAttivitaIdAllegatiIdContenutoBinarioBody;
export type PostBackAttivitaIdAllegatiIdContenutoBinarioMutationError =
  ErrorType<unknown>;

/**
 * @summary Upload allegato
 */
export const usePostBackAttivitaIdAllegatiIdContenutoBinario = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackAttivitaIdAllegatiIdContenutoBinario>>,
    TError,
    {
      idTipoAllegato: string;
      data: PostBackAttivitaIdAllegatiIdContenutoBinarioBody;
      params: PostBackAttivitaIdAllegatiIdContenutoBinarioParams;
      idTaskInstance?: string;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackAttivitaIdAllegatiIdContenutoBinarioMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce un allegato in formato binario
 * @summary Download allegato
 */
export const getBackAttivitaIdAllegatiIdContenutoBinario = (
  idAllegato: string,
  params?: GetBackAttivitaIdAllegatiIdContenutoBinarioParams,
  idTaskInstance = 'procedimento',
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati/${idAllegato}/contenutoBinario`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackAttivitaIdAllegatiIdContenutoBinarioQueryKey = (
  idAllegato: string,
  params?: GetBackAttivitaIdAllegatiIdContenutoBinarioParams,
  idTaskInstance = 'procedimento',
) =>
  [
    `/framework-bo-procedimenti/attivita/${idTaskInstance}/allegati/${idAllegato}/contenutoBinario`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackAttivitaIdAllegatiIdContenutoBinarioQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>
  >,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  params?: GetBackAttivitaIdAllegatiIdContenutoBinarioParams,
  idTaskInstance = 'procedimento',
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackAttivitaIdAllegatiIdContenutoBinarioQueryKey(
      idAllegato,
      params,
      idTaskInstance,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>>
  > = ({ signal }) =>
    getBackAttivitaIdAllegatiIdContenutoBinario(
      idAllegato,
      params,
      idTaskInstance,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idTaskInstance && idAllegato),
    ...queryOptions,
  };
};

export type GetBackAttivitaIdAllegatiIdContenutoBinarioQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>>
  >;
export type GetBackAttivitaIdAllegatiIdContenutoBinarioQueryError =
  ErrorType<unknown>;

/**
 * @summary Download allegato
 */
export const useGetBackAttivitaIdAllegatiIdContenutoBinario = <
  TData = Awaited<
    ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>
  >,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  params?: GetBackAttivitaIdAllegatiIdContenutoBinarioParams,
  idTaskInstance = 'procedimento',
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackAttivitaIdAllegatiIdContenutoBinario>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackAttivitaIdAllegatiIdContenutoBinarioQueryOptions(
      idAllegato,
      params,
      idTaskInstance,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
